import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { PricingAnalysisRow } from '@/types/competitor';
import { format, parseISO } from 'date-fns';
import { useCurrencyFormat } from '@/hooks/use-currency-format';

interface PriceComparisonChartProps {
  data: PricingAnalysisRow[];
  numberOfDays?: number;
}

export function PriceComparisonChart({ data, numberOfDays = 15 }: PriceComparisonChartProps) {
  const { format: formatPrice } = useCurrencyFormat();
  const chartData = useMemo(() => {
    return data.map(row => ({
      date: format(parseISO(row.date), 'MMM dd'),
      fullDate: row.date,
      yourPrice: row.yourPrice || 0,
      marketAvg: row.marketAvg || 0,
      suggested: row.suggestedPrice || row.marketAvg || 0,
      occupancy: row.occupancy,
    }));
  }, [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Extract values from the first payload item's payload object
      const data = payload[0]?.payload;
      const yourPrice = data?.yourPrice || 0;
      const marketAvg = data?.marketAvg || 0;
      const suggested = data?.suggested || 0;
      const diff = yourPrice && marketAvg ? yourPrice - marketAvg : 0;
      
      return (
        <div className="rounded-lg border bg-background p-3 shadow-lg">
          <p className="font-medium text-sm mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-primary" />
              <span className="text-muted-foreground">Your Price:</span>
              <span className="font-medium">{formatPrice(yourPrice)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-destructive" />
              <span className="text-muted-foreground">Market Avg:</span>
              <span className="font-medium">{formatPrice(marketAvg)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              <span className="text-muted-foreground">Suggested:</span>
              <span className="font-medium">{formatPrice(suggested)}</span>
            </div>
            <div className="border-t mt-2 pt-2">
              <span className={`text-xs font-medium ${diff > 0 ? 'text-destructive' : diff < 0 ? 'text-green-600' : ''}`}>
                {diff > 0 ? `${formatPrice(diff)} above market` : diff < 0 ? `${formatPrice(Math.abs(diff))} below market` : 'At market rate'}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">{numberOfDays}-Day Price Comparison</CardTitle>
        <p className="text-sm text-muted-foreground">Your price vs competitor average over time</p>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorYourPrice" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorMarketAvg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => formatPrice(value)}
                className="text-muted-foreground"
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                formatter={(value) => {
                  const labels: Record<string, string> = {
                    yourPrice: 'Your Price',
                    marketAvg: 'Market Average',
                    suggested: 'Suggested Price',
                  };
                  return <span className="text-sm">{labels[value] || value}</span>;
                }}
              />
              <Area
                type="monotone"
                dataKey="yourPrice"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorYourPrice)"
              />
              <Area
                type="monotone"
                dataKey="marketAvg"
                stroke="hsl(var(--destructive))"
                strokeWidth={2}
                strokeDasharray="5 5"
                fillOpacity={1}
                fill="url(#colorMarketAvg)"
              />
              <Line
                type="monotone"
                dataKey="suggested"
                stroke="#22c55e"
                strokeWidth={2}
                dot={{ r: 3, fill: '#22c55e' }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
